// src/App.js

import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import api from "./api/api";
import { AuthProvider, useAuth } from "./context/AuthContext";
// CART CONTEXT AND COMPONENT
import { CartProvider } from "./context/CartContext";
import CartSideBar from "./components/CartSideBar";
// ----------------------------------------

import Home from "./pages/Home";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import OrderSuccess from "./pages/OrderSuccess";
import Wheatbag from "./pages/Wheatbag";
import Wallarts from "./pages/Wallarts";
import ProductDetail from "./pages/ProductDetail";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Policy from "./pages/Policy";
import MainLayout from "./layouts/MainLayout";
import AuthHandler from "./components/AuthHandler";
import Products from "./components/Products";

// User-facing pages
import Login from "./user/pages/Login";
import Signup from "./user/pages/Signup";
import ForgotPassword from "./user/pages/ForgotPassword";
import ResetPassword from "./user/pages/ResetPassword";
import UserProfile from "./user/pages/UserProfile";
import EditProfile from "./user/pages/EditProfile";
import ChangePassword from "./user/pages/ChangePassword";
import OrderDetailsPage from "./user/pages/OrderDetails";

// Admin pages
import AdminLogin from "./admin/pages/Login";
import Dashboard from "./admin/pages/Dashboard";
import AdminOrder from "./admin/pages/Orders";
import OrderDetails from "./admin/pages/OrderDetails";
import ListProducts from "./admin/pages/ListProducts";
import CreateProducts from "./admin/pages/CreateProducts";
import EditProductsPage from "./admin/pages/EditProductsPage";
import { isAdmin } from "./admin/utils/auth";
import routes from "./routes";
import ReviewModeration from "./admin/ReviewModeration";
import { useTranslation } from "react-i18next";
import { ENABLE_CART_UI, ENABLE_CHECKOUT_UI } from "./config/featureFlags";

// Component to protect routes for logged-in users
const ProtectedRoute = ({ children }) => {
  const { isLoggedIn, loading } = useAuth();
  if (loading) {
    return <div>Loading...</div>;
  }
  return isLoggedIn ? children : <Navigate to={routes.login} />;
};

// Component to protect routes for admin users
const AdminRoute = ({ children }) => {
  return isAdmin() ? children : <Navigate to="/dashboard-admin/login" />;
};

function App() {
  const { t } = useTranslation();
  useEffect(() => {
    // This is just a test call, you can remove it if your API is working
    api.get("/products").catch((err) => console.error(err));
  }, []);

  return (
    <AuthProvider>
      {/* CartProvider must be inside AuthProvider so it can consume auth session state via useAuth.
       */}
      <CartProvider>
        <Router>
          {/* Global components (like the sidebar) go here, outside <Routes> */}
          <AuthHandler />
          {ENABLE_CART_UI && <CartSideBar />}

          <Routes>
            {/* Public Routes */}
            <Route
              path="/"
              element={
                <MainLayout>
                  <Home />
                </MainLayout>
              }
            />
            <Route
              path="/wheatbag"
              element={
                <MainLayout>
                  <Wheatbag />
                </MainLayout>
              }
            />

            <Route
              path="/cart"
              element={
                ENABLE_CART_UI ? (
                  <MainLayout>
                    <Cart />
                  </MainLayout>
                ) : (
                  <Navigate to={routes.home} replace />
                )
              }
            />
            <Route
              path="/checkout"
              element={
                ENABLE_CHECKOUT_UI ? (
                  <MainLayout>
                    <Checkout />
                  </MainLayout>
                ) : (
                  <Navigate to={routes.home} replace />
                )
              }
            />
            <Route
              path="/product/:productId"
              element={
                <MainLayout>
                  <ProductDetail />
                </MainLayout>
              }
            />

            <Route
              path="/products"
              element={
                <MainLayout>
                  <Products
                    title={t("products.routes.all.title")}
                    subtitle={t("products.routes.all.subtitle")}
                  />
                </MainLayout>
              }
            />

            <Route
              path="/fridgeDeo"
              element={
                <MainLayout>
                  <Products
                    tag="fridge"
                    title={t("products.routes.fridge.title")}
                    subtitle={t("products.routes.fridge.subtitle")}
                  />
                </MainLayout>
              }
            />

            <Route
              path="/giftProducts"
              element={
                <MainLayout>
                  <Products
                    pageKey="gift"
                    title={t("products.routes.gifts.title")}
                    subtitle={t("products.routes.gifts.subtitle")}
                  />
                </MainLayout>
              }
            />

            <Route
              path="/naturalProducts"
              element={
                <MainLayout>
                  <Products
                    pageKey="natural"
                    title={t("products.routes.natural.title")}
                    subtitle={t("products.routes.natural.subtitle")}
                  />
                </MainLayout>
              }
            />

            <Route
              path="/campingOutdoors"
              element={
                <MainLayout>
                  <Products
                    pageKey="camping"
                    title={t("products.routes.camping.title")}
                    subtitle={t("products.routes.camping.subtitle")}
                  />
                </MainLayout>
              }
            />

            <Route
              path="/kitchenProducts"
              element={
                <MainLayout>
                  <Products
                    pageKey="kitchen"
                    title={t("products.routes.kitchen.title")}
                    subtitle={t("products.routes.kitchen.subtitle")}
                  />
                </MainLayout>
              }
            />

            <Route
              path="/japaneseProducts"
              element={
                <MainLayout>
                  <Products
                    tag="japanese"
                    title={t("products.routes.japanese.title")}
                    subtitle={t("products.routes.japanese.subtitle")}
                  />
                </MainLayout>
              }
            />

            <Route
              path="/handmadeProducts"
              element={
                <MainLayout>
                  <Products
                    pageKey="handmade"
                    title={t("products.routes.handmade.title")}
                    subtitle={t("products.routes.handmade.subtitle")}
                  />
                </MainLayout>
              }
            />

            <Route
              path="/wallarts"
              element={
                <MainLayout>
                  <Wallarts />
                </MainLayout>
              }
            />

            <Route path="/order/success/:orderId" element={<OrderSuccess />} />
            <Route
              path={routes.aboutUs}
              element={
                <MainLayout>
                  <About />
                </MainLayout>
              }
            />
            <Route
              path={routes.policies}
              element={
                <MainLayout>
                  <Policy />
                </MainLayout>
              }
            />
            <Route
              path="/contact-us"
              element={
                <MainLayout>
                  <Contact />
                </MainLayout>
              }
            />

            {/* Public Auth Routes */}
            <Route
              path={routes.login}
              element={
                <MainLayout>
                  <Login />
                </MainLayout>
              }
            />
            <Route
              path={routes.signup}
              element={
                <MainLayout>
                  <Signup />
                </MainLayout>
              }
            />
            <Route
              path={routes.forgotPassword}
              element={
                <MainLayout>
                  <ForgotPassword />
                </MainLayout>
              }
            />
            <Route
              path={routes.resetPassword}
              element={
                <MainLayout>
                  <ResetPassword />
                </MainLayout>
              }
            />

            {/* Protected User Routes */}
            <Route
              path={routes.profile}
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <UserProfile />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path={routes.editProfile}
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <EditProfile />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path={routes.changePassword}
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <ChangePassword />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/user/orders/:orderId"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <OrderDetailsPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            {/* Admin Routes */}
            <Route path="/dashboard-admin/login" element={<AdminLogin />} />
            <Route
              path="/dashboard-admin"
              element={
                <AdminRoute>
                  <Dashboard />
                </AdminRoute>
              }
            />
            <Route
              path="/dashboard-admin/products"
              element={
                <AdminRoute>
                  <ListProducts />
                </AdminRoute>
              }
            />
            <Route
              path="/dashboard-admin/orders"
              element={
                <AdminRoute>
                  <AdminOrder />
                </AdminRoute>
              }
            />
            <Route
              path="/dashboard-admin/orders/:orderId"
              element={
                <AdminRoute>
                  <OrderDetails />
                </AdminRoute>
              }
            />
            <Route
              path="/dashboard-admin/products/new"
              element={
                <AdminRoute>
                  <CreateProducts />
                </AdminRoute>
              }
            />
            <Route
              path="/dashboard-admin/products/edit/:id"
              element={
                <AdminRoute>
                  <EditProductsPage />
                </AdminRoute>
              }
            />
            <Route
              path="/dashboard-admin/reviews"
              element={
                <AdminRoute>
                  <ReviewModeration />
                </AdminRoute>
              }
            />
          </Routes>
        </Router>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
